import React from "react";
import { Input } from "lib/ui";
import { SearchOutlined } from "lib/icons";

/** Purple hero header used across admin CRUD pages */
export function AdminHero({ icon: Icon, badge, title, description, actions }) {
  return (
    <header className="relative overflow-hidden rounded-2xl border border-violet-200/60 bg-gradient-to-br from-brand-deeper via-brand-dark to-brand px-6 py-7 text-white shadow-[0_20px_50px_-12px_rgba(91,33,182,0.4)] md:px-8">
      <div className="relative flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
        <div className="min-w-0">
          {badge ? (
            <p className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-violet-100 ring-1 ring-white/15">
              {Icon ? <Icon size={14} strokeWidth={2.25} aria-hidden /> : null}
              {badge}
            </p>
          ) : null}
          <h1 className="mt-3 text-2xl font-extrabold tracking-tight md:text-3xl">
            {title}
          </h1>
          {description ? (
            <p className="mt-2 max-w-xl text-sm leading-relaxed text-violet-100/90">
              {description}
            </p>
          ) : null}
        </div>
        {actions ? (
          <div className="flex shrink-0 flex-wrap gap-2">{actions}</div>
        ) : null}
      </div>
    </header>
  );
}

export function AdminStats({ items = [] }) {
  return (
    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
      {items.map(({ label, value, icon: Icon }) => (
        <div
          key={label}
          className="rounded-2xl border border-slate-200/80 bg-white p-4 shadow-card"
        >
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500">
              {label}
            </p>
            {Icon ? (
              <span className="rounded-lg bg-brand-surface p-2 text-brand">
                <Icon size={18} strokeWidth={2.25} />
              </span>
            ) : null}
          </div>
          <p className="mt-2 text-2xl font-extrabold tabular-nums text-slate-900">
            {value}
          </p>
        </div>
      ))}
    </div>
  );
}

export function AdminSearchBar({
  value,
  onChange,
  placeholder = "Search…",
  extra,
}) {
  return (
    <div className="space-y-3 rounded-2xl border border-slate-200/80 bg-white p-4 shadow-card">
      {extra}
      <Input
        placeholder={placeholder}
        prefix={<SearchOutlined />}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full"
      />
    </div>
  );
}

export function AdminFilterTabs({ tabs, active, onChange }) {
  return (
    <div
      className={`grid gap-1.5 rounded-xl bg-slate-100 p-1.5 ${
        tabs.length <= 2
          ? "grid-cols-2"
          : tabs.length === 3
            ? "grid-cols-1 sm:grid-cols-3"
            : "grid-cols-2 sm:grid-cols-4"
      }`}
      role="tablist"
    >
      {tabs.map(({ id, label }) => (
        <button
          key={id}
          type="button"
          role="tab"
          aria-selected={active === id}
          onClick={() => onChange(id)}
          className={`rounded-lg px-3 py-2.5 text-sm font-semibold transition ${
            active === id
              ? "bg-white text-brand-dark shadow-sm ring-1 ring-slate-200/80"
              : "text-slate-600 hover:bg-white/60 hover:text-slate-900"
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

export function AdminPageLayout({ children }) {
  return <div className="space-y-6 pb-10">{children}</div>;
}

export function AdminFormPanel({ title, description, children, footer }) {
  return (
    <section className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-card">
      {(title || description) && (
        <header className="border-b border-slate-100 px-6 py-4">
          {title ? (
            <h2 className="text-lg font-bold text-slate-900">{title}</h2>
          ) : null}
          {description ? (
            <p className="mt-1 text-sm text-slate-500">{description}</p>
          ) : null}
        </header>
      )}
      <div className="p-6">{children}</div>
      {footer ? (
        <footer className="border-t border-slate-100 bg-slate-50/80 px-6 py-4">
          {footer}
        </footer>
      ) : null}
    </section>
  );
}

export function AdminFormSection({ title, children }) {
  return (
    <div className="rounded-xl border border-slate-200/80 bg-slate-50/50 p-5">
      {title ? (
        <h3 className="mb-4 text-sm font-bold uppercase tracking-wider text-slate-600">
          {title}
        </h3>
      ) : null}
      {children}
    </div>
  );
}
